import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, deliveryApi, rememberManagementToken } from '../api';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * TICK-061 — where an email Magic Link lands.
 *
 * The consume is a POST issued by this page's JavaScript rather than something the link itself
 * performs. Corporate mail scanners and link-preview bots fetch every URL in an email before the
 * recipient sees it; if opening the URL were enough to spend the single use, the customer's own
 * click would routinely arrive second and be told their link was already used. A scanner gets HTML
 * and runs no script, so it leaves the challenge untouched.
 */
export default function EmailVerify() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [eventSlug, setEventSlug] = useState<string | null>(null);
  usePageTitle(eventSlug, 'ยืนยันอีเมล');

  const [status, setStatus] = useState<'working' | 'verified' | 'failed'>('working');
  const [message, setMessage] = useState('');
  const [orderPath, setOrderPath] = useState('');
  // React 18 StrictMode mounts effects twice in development. Without this guard the second run
  // would consume the challenge the first one just claimed and report it as already used.
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !token) return;
    started.current = true;
    deliveryApi.verifyEmail(token)
      .then((result) => {
        setStatus('verified');
        // A recovery link mints a replacement capability; keeping it is what puts the customer back
        // in control of their own delivery settings on this device.
        if (result.managementToken && result.state) {
          rememberManagementToken(result.state.orderCode, result.managementToken);
        }
        // The event slug has to be in the path. `/order/:code` is a redirect to the default event's
        // landing page, so on any other tenant it sent the customer to the wrong event entirely.
        if (result.state?.eventSlug) {
          setEventSlug(result.state.eventSlug);
          setOrderPath(`/e/${encodeURIComponent(result.state.eventSlug)}/order/${encodeURIComponent(result.state.orderCode)}`);
        }
        setMessage(result.alreadyVerified
          ? 'อีเมลนี้ยืนยันไว้แล้ว — ไม่ต้องทำอะไรเพิ่ม'
          : 'ยืนยันอีเมลสำเร็จ บัตรจะถูกส่งไปที่อีเมลนี้เมื่อตรวจสอบการชำระเงินเสร็จ');
      })
      .catch((err) => {
        setStatus('failed');
        setMessage(err instanceof ApiError
          ? err.message
          : 'ยืนยันอีเมลไม่สำเร็จ กรุณาขอลิงก์ใหม่จากหน้าคำสั่งซื้อ');
      });
  }, [token]);

  return (
    <div className="page success-wrap" style={{ paddingTop: 120 }}>
      <div className="mono-label" style={{ letterSpacing: '0.2em', marginBottom: 14 }}>EMAIL VERIFICATION</div>
      <h1>
        {status === 'working' ? 'กำลังยืนยันอีเมล…' : status === 'verified' ? 'ยืนยันอีเมลแล้ว ✓' : 'ยืนยันอีเมลไม่สำเร็จ'}
      </h1>
      <p aria-live="polite">{message || 'กรุณารอสักครู่'}</p>
      {status !== 'working' && (
        <button className="btn-primary" style={{ marginTop: 12 }} onClick={() => navigate(orderPath || '/')}>
          {orderPath ? 'ไปหน้าคำสั่งซื้อ' : 'กลับหน้าแรก'}
        </button>
      )}
    </div>
  );
}
